import React, { useMemo, useState, useEffect, createContext, useContext, useCallback, } from 'react';
import { ThemeContainer } from '@sas/nova-commons/theme';
import { connect } from 'react-redux';
import { getReportThemeParameters, resolveReportThemeOverrides, isSystemReportTheme, } from '@sas/nova-commons/design-tokens';
import { fetchThemeParameters, fetchOfflineThemeParameters } from './internal/ThemeService';
import { TransportState } from '../../store/transport/transportState';
import { bindActionCreators } from 'redux';
import { useHasHighContrastReportTheme, useLoadingTheme } from '../../config';
import cx from 'classnames';
import cn from './ReportThemeContainer.css.json';
import './ReportThemeContainer.css';
const LoadingThemeContext = createContext([
    getReportThemeParameters('light'),
    undefined,
]);
export const useLoadingThemeContext = () => {
    return useContext(LoadingThemeContext);
};
export const useLoadingBusyTokens = () => {
    const [, style] = useLoadingThemeContext();
    const busyTokens = useCallback(() => {
        const overrides = {};
        if (style === null || style === void 0 ? void 0 : style.backgroundColor) {
            overrides.bg = style.backgroundColor;
        }
        if (style === null || style === void 0 ? void 0 : style.textColor) {
            overrides.fg = style.textColor;
        }
        return overrides;
    }, [style]);
    const animationTokens = useMemo(() => {
        const bg = style === null || style === void 0 ? void 0 : style.primary;
        if (!bg) {
            return undefined;
        }
        return () => ({
            bg,
        });
    }, [style]);
    return [animationTokens, busyTokens];
};
const ReportThemeContainerBase = ({ reportTheme, children, url, packageUri, themeOverrides, authenticated, actions, className, style, }) => {
    const defaultThemeName = useHasHighContrastReportTheme() ? 'highcontrast2020' : 'light';
    const initialTheme = useMemo(() => getReportThemeParameters(reportTheme !== null && reportTheme !== void 0 ? reportTheme : defaultThemeName), [
        reportTheme,
        defaultThemeName,
    ]);
    const [serverTheme, setServerTheme] = useState(null);
    const overrideTheme = useMemo(() => {
        return themeOverrides ? resolveReportThemeOverrides(themeOverrides) : null;
    }, [themeOverrides]);
    const currentTheme = overrideTheme || serverTheme || initialTheme;
    const loadingTheme = useLoadingTheme();
    const loadingValue = useMemo(() => {
        if (!loadingTheme) {
            return [currentTheme, undefined];
        }
        if (typeof loadingTheme === 'string') {
            return [getReportThemeParameters(loadingTheme), undefined];
        }
        let theme = getReportThemeParameters(loadingTheme.baseTheme || 'light');
        if (loadingTheme.fontFamily) {
            theme = Object.assign(Object.assign({}, theme), { overrides: Object.assign(Object.assign({}, theme.overrides), { fontFamily: loadingTheme.fontFamily }) });
        }
        return [theme, loadingTheme];
    }, [loadingTheme, currentTheme]);
    useEffect(() => {
        if (serverTheme && !reportTheme) {
            setServerTheme(null);
        }
    }, [serverTheme, reportTheme]);
    useEffect(() => {
        if (!reportTheme) {
            return;
        }
        const isLocalTheme = !!overrideTheme || isSystemReportTheme(reportTheme);
        if (isLocalTheme) {
            actions.setThemeLoaded(true);
            setServerTheme(null);
            return;
        }
        if (!authenticated) {
            return;
        }
        let ignoreResult = false;
        if (packageUri) {
            fetchOfflineThemeParameters(packageUri, reportTheme)
                .then((theme) => {
                if (ignoreResult) {
                    return;
                }
                setServerTheme(theme);
                actions.setThemeLoaded(true);
            })
                .catch(() => {
                if (ignoreResult) {
                    return;
                }
                actions.setTransportErrorKey({ key: 'ReportContainer.themeLoadFailed.txt' });
            });
        }
        else if (url) {
            fetchThemeParameters(url, reportTheme)
                .then((theme) => {
                if (ignoreResult) {
                    return;
                }
                setServerTheme(theme);
                actions.setThemeLoaded(true);
            })
                .catch(() => {
                if (ignoreResult) {
                    return;
                }
                actions.setTransportErrorKey({ key: 'ReportContainer.themeLoadFailed.txt' });
            });
        }
        else {
            return;
        }
        return () => {
            ignoreResult = true;
        };
    }, [authenticated, url, packageUri, reportTheme, overrideTheme, actions]);
    return (React.createElement(ThemeContainer, { className: cx(cn.themeContainer, className), style: style, currentTheme: currentTheme },
        React.createElement(LoadingThemeContext.Provider, { value: loadingValue }, children)));
};
export const ReportThemeContainer = connect((state, _props) => ({
    reportTheme: state.bird.reportTheme,
    authenticated: state.transport.reportLoaded === true,
}), (dispatch) => ({ actions: bindActionCreators(TransportState.actions, dispatch) }))(ReportThemeContainerBase);
